import { FiBox, FiX } from 'react-icons/fi'
import Router from 'next/router'
import useSWRInfinite from 'swr/infinite'
import { Virtuoso } from 'react-virtuoso'
import dayjs from 'dayjs'
import fetcher from '@byma/shared/helpers/fetcher'
import Loader from '@byma/shared/components/Loader'
import Input from '@byma/shared/components/Input'
import React from 'react'

function CouponItem({coupon, active=false}) {
    return (
        <div className={`flex items-center cursor-pointer box box-hover ${active && 'box-active'} gap-3`}>
            <input type="checkbox" checked={active} readOnly />
            { coupon.partyId?.pictures?.photos[0] ? (
                <img src={coupon.partyId.pictures.photos[0]} alt={coupon.partyId.name} className="rounded-full w-12 h-12 object-cover" />
            ) : (
                <div className="w-12 h-12 rounded-full bg-gray-400 overflow-hidden" />
            ) }
            <div className="">
                <p className="text-md font-bold">{coupon?.code}</p>
                <p className="text-sm">{coupon?.partyId?.name}</p>
            </div>
        </div>
    )
}

function toQueryString(obj, forceKey=null) {
    return Object.keys(obj).map(function (key) {
        if (typeof obj[key] === 'object') {
            return toQueryString(obj[key], key)
        }
        return (forceKey || key) + '=' + obj[key]
    }).join('&');
}

export default function CouponFilter({ token, filteredCoupons=[] }) {
    const [couponFilterOpen, setCouponFilterOpen] = React.useState(false);
    const [selectedCoupons, setSelectedCoupons] = React.useState([])
    const [searchQuery, setSearchQuery] = React.useState('')

    function getKey(pageIndex, previousPageData) {
        if (!token) return null
        if (previousPageData?.result?.length === 0) return null
        if (pageIndex === 0) {
            return [`/api/coupons/getAll?${toQueryString({search: searchQuery, perPage: 20})}`, null, token]
        }
        const lastResult = previousPageData?.result[previousPageData.result.length - 1]
        if (!lastResult) return null
        return [`/api/coupons/getAll?${toQueryString({toDate: lastResult.created_at, search: searchQuery, perPage: 20})}`, null, token]
    }

    const { data: coupons, isValidating, size, setSize } = useSWRInfinite(getKey, fetcher)

    async function handleSearch(e) {
        e.preventDefault()
        setSearchQuery(e.currentTarget.search.value)
    }

    const couponData = React.useMemo(() => {
        if (!coupons) {
            return []
        }

        return coupons.reduce((acc, cur) => {
            return [...acc, ...(cur?.result || [])]
        }, [])
    }, [coupons])


    React.useEffect(() => {
        if (filteredCoupons) {
            setSelectedCoupons(typeof filteredCoupons === 'string' ? [filteredCoupons] : filteredCoupons)
        }
    }, [filteredCoupons])

    function handleSelect(coupon) {
        if (selectedCoupons.indexOf(coupon._id) >= 0) {
            setSelectedCoupons(selectedCoupons.filter(e => e !== coupon._id))
        } else {
            setSelectedCoupons([...selectedCoupons, coupon._id])
        }
    }

    function handleFilter() {
        let url = new URL(window.location.href)
        let searchParams = url.searchParams
        searchParams.delete('cp')
        selectedCoupons.forEach(event => searchParams.append('cp', event))
        url.search = searchParams.toString()
        Router.push(url.toString())
        setCouponFilterOpen(false)
    }

    function rowRenderer(index, data) {
        return (
            <div onClick={() => handleSelect(couponData[index])} key={index}>
                <CouponItem coupon={couponData[index]} active={selectedCoupons.indexOf(couponData[index]?._id) >=0} />
            </div>
        )
    }

    return (
        <div className="">
            <button className="flex justify-between items-center cursor-pointer rounded shadow px-2 py-1 text-sm hover:shadow-md whitespace-nowrap space-x-1 focus:shadow-sm" onClick={() => setCouponFilterOpen(!couponFilterOpen)}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                {selectedCoupons.length > 0 ? (
                    <p>
                        {selectedCoupons.length} cupo{selectedCoupons.length > 1 ? 'ns' : 'm'}
                    </p>
                ) : (
                    <p>
                        Todos os cupons 
                    </p>
                )}
            </button>
            <div className={`fixed w-full h-full inset-0 transition-opacity duration-100 ${couponFilterOpen ? 'z-50 opacity-100' : '-z-1 opacity-0'}`} onClick={() => setCouponFilterOpen(false)}>
                <div class="absolute w-full h-full bg-gray-900 opacity-50" />
                <div class="modal-content absolute bg-white w-full md:w-11/12 md:max-w-md md:mx-auto md:rounded md:shadow-lg z-50 md:h-3/4 h-full" onClick={(e) => e.stopPropagation()}>
                    <div className="flex flex-col justify-between p-3 space-y-3 h-full">
                        <div className="">
                            <div className="flex justify-between items-center mb-3">
                                <div className="">
                                    <p className="font-bold text-xl">Filtrar por cupom</p>
                                </div>
                                <div className="cursor-pointer" onClick={() => setCouponFilterOpen(false)}>
                                    <FiX size={24}/>
                                </div>
                            </div>
                        </div>
                        <form onSubmit={handleSearch}>
                            <Input size="s" name="search" type="text" label={(
                                <div className="flex space-x-2 items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                                    </svg>
                                    <p>Buscar código ou email de disparo...</p>
                                </div>
                            )} />
                        </form>
                        <div className="flex flex-col h-full">
                            <Virtuoso
                                data={couponData}
                                endReached={() => setSize(size+1)}
                                overscan={200}
                                itemContent={rowRenderer}
                                components={{
                                    Header: () => {
                                        return (
                                            <div
                                                className="h-2"
                                            />
                                        )
                                    },
                                    Footer: () => {
                                        return (
                                            <div
                                                className="p-3 flex items-center justify-center"
                                            >
                                                { isValidating ? (
                                                    <Loader /> 
                                                ): (
                                                    <p className="text-sm text-gray-700">
                                                        { couponData?.length === 0 ? 'Nenhum cupom encontrado' : 'Fim dos cupons' }
                                                    </p>
                                                )}
                                            </div>
                                        )
                                    }
                                }}
                            />
                        </div>
                        <div className="flex space-x-2">
                            {selectedCoupons.length === 0 ? (
                                <button className="btn btn-primary w-full" onClick={handleFilter}>Remover filtro de cupons</button>
                            ) : (
                                <>
                                    <button className="btn btn-primary w-full" onClick={handleFilter}>Filtrar {selectedCoupons.length} cupo{selectedCoupons.length > 1 ? 'ns' : 'm'}</button>
                                    <button className="btn btn-white w-full" onClick={() => setSelectedCoupons([])}>Limpar sele&ccedil;&atilde;o</button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
